const normalizeReference = (value) => String(value ?? '')
  .trim()
  .replace(/\s+/g, ' ')
  .toLowerCase();

const booleanAllocationsByReference = (allocations) => {
  if (!allocations || typeof allocations !== 'object' || Array.isArray(allocations)) {
    return new Map();
  }

  const values = new Map();
  Object.entries(allocations).forEach(([reference, allocated]) => {
    if (typeof allocated !== 'boolean') return;

    const normalizedReference = normalizeReference(reference);
    if (!normalizedReference || normalizedReference === 'undefined') return;

    const existing = values.get(normalizedReference);
    if (existing === undefined || existing === allocated) {
      values.set(normalizedReference, allocated);
    } else {
      // Conflicting legacy aliases are unsafe to infer. A canonical ID, when
      // present, is still considered separately below.
      values.set(normalizedReference, null);
    }
  });
  return values;
};

const uniqueAliases = (locations, field) => {
  const owners = new Map();
  locations.forEach((location) => {
    const alias = normalizeReference(location?.[field]);
    if (!alias) return;

    const locationId = String(location.location_id).trim();
    if (!owners.has(alias)) {
      owners.set(alias, locationId);
    } else if (owners.get(alias) !== locationId) {
      owners.set(alias, null);
    }
  });
  return owners;
};

/**
 * Restrict a User location permission map to locations loaded for the current
 * home account. The API returns canonical tenant-qualified `location_id`
 * values; legacy short codes and names are read only when they identify one
 * current location unambiguously. The returned object always uses canonical
 * IDs, so stale, deleted, foreign, malformed and placeholder keys are dropped.
 */
export const canonicalizeLocationAllocations = (locations, allocations) => {
  const currentLocations = Array.isArray(locations)
    ? locations.filter((location) => String(location?.location_id ?? '').trim())
    : [];
  const savedValues = booleanAllocationsByReference(allocations);
  const codeOwners = uniqueAliases(currentLocations, 'location_code');
  const nameOwners = uniqueAliases(currentLocations, 'location_name');

  return currentLocations.reduce((canonical, location) => {
    const locationId = String(location.location_id).trim();
    const canonicalReference = normalizeReference(locationId);
    const codeReference = normalizeReference(location.location_code);
    const nameReference = normalizeReference(location.location_name);

    let allocated = savedValues.get(canonicalReference);
    if (typeof allocated !== 'boolean'
        && codeReference
        && codeOwners.get(codeReference) === locationId) {
      allocated = savedValues.get(codeReference);
    }
    if (typeof allocated !== 'boolean'
        && nameReference
        && nameOwners.get(nameReference) === locationId) {
      allocated = savedValues.get(nameReference);
    }

    canonical[locationId] = allocated === true;
    return canonical;
  }, {});
};

export const areAllCurrentLocationsAllocated = (locations, allocations) => {
  const canonical = canonicalizeLocationAllocations(locations, allocations);
  const values = Object.values(canonical);
  return values.length > 0 && values.every(Boolean);
};
